import axios from 'axios';
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import get from 'lodash/get';
import type { User } from '@vmos2/core';
import { BugsnagClient, Profile, storage, userApi, userService } from '@vmos2/core';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DeepPartial } from 'redux';

import { useSettings } from './Settings';
import { useHybridApp } from '@vmos2/utils';

type ProfileState = {
  profileData: (Partial<PushNotificationData> & Partial<Profile>) | null,
  profileLoading: boolean,
};

type PushNotificationData = { pushNotificationEnabled: boolean, pushNotificationToken: string };

type UserState = {
  userData?: DeepPartial<User> | null,
};

type UserProviderContext = {
  getUserProfile: () => void,
  isGuest: boolean,
  isLoggedIn: boolean,
  logout: () => void,
  phoneVerification: { enabled: boolean },
  profile: ProfileState,
  updateProfilePostcode: (postcode: string) => void,
  updateUserLocale: (locale: string) => void,
  updateUserPushNotifications: (pushNotificationData: PushNotificationData) => void,
  user: { userData?: UserState['userData'], userLoading: boolean },
};

const UserContext = createContext<UserProviderContext | undefined>(undefined);

type ProviderProps = {
  children: React.ReactNode,
};

export const UserProvider = ({ children }: ProviderProps): ReactElement => {
  const abortController = new AbortController();
  const { hybridApp, isLoading } = useHybridApp();
  const [user, setUser] = useState<UserState>({ userData: undefined });
  const [userLoading, setUserLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileState>({ profileData: null, profileLoading: true });
  const { phoneVerification: phoneVerificationSetting, qrCodeEnabled } = useSettings();
  const { t } = useTranslation('header');
  const { userData } = user;
  const isGuest = userData?.role?.slug === 'guest';
  const isLoggedIn = !!userData && !isGuest;
  const phoneVerification = { enabled: get(phoneVerificationSetting, 'enabled', false) };
  const memberNumber = profile.profileData?.memberNumber;
  const fallbackFirstName = t('noName');

  const logout = useCallback(() => userService.logout(), []);

  const updateProfilePostcode = useCallback(
    async (postcode: string) => {
      if (userData) {
        await userApi.patch('v1/user/profile', { postcode, address: null });

        setProfile(previousProfile => ({
          ...previousProfile,
          profileData: { ...previousProfile.profileData, postcode },
        }));
      }
    },
    [setProfile, userData]
  );

  const updateUserLocale = useCallback((locale: string) => userApi.patch('v1/user', { locale }), []);

  const getUserProfile = useCallback(async () => {
    userApi
      .get('v1/user/profile', { cache: { ttl: 3600 * 1000 }, id: 'user-profile' })
      .then(data => {
        if (!abortController.signal.aborted) {
          if (!data.firstName) {
            data.firstName = fallbackFirstName;
          }

          setProfile(previousProfile => ({ ...previousProfile, profileData: data }));
        }
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setProfile(previousProfile => ({ ...previousProfile, profileLoading: false }));
        }
      });
  }, []);

  const updateUserPushNotifications = useCallback(
    async (pushNotificationData: PushNotificationData) => {
      try {
        if (isLoggedIn && pushNotificationData) {
          const { pushNotificationEnabled, pushNotificationToken } = pushNotificationData;

          if (
            profile?.profileData?.pushNotificationEnabled === pushNotificationEnabled &&
            profile?.profileData?.pushNotificationToken === pushNotificationToken
          ) {
            return;
          }

          await userService.updateUserProfile({ pushNotificationEnabled, pushNotificationToken }, true);

          setProfile(existingProfile => ({
            ...existingProfile,
            profileData: {
              ...existingProfile?.profileData,
              pushNotificationEnabled,
              pushNotificationToken,
            },
          }));
        }
      } catch (error) {
        BugsnagClient.notify('User profile was not updated successfully. Error: ', { error });
      }
    },
    [isLoggedIn, profile?.profileData?.pushNotificationEnabled, profile?.profileData?.pushNotificationToken]
  );

  useEffect(() => {
    if (
      phoneVerification?.enabled &&
      !!userData &&
      !userData?.profile?.isConfirmed &&
      userData?.role?.slug === 'customer'
    ) {
      logout();
    }
  }, [phoneVerification?.enabled, userData?.profile?.isConfirmed, userData?.role?.slug]);

  useEffect(() => {
    getUserProfile();

    return () => {
      abortController.abort('Component was unmounted');
    };
  }, []);

  useEffect(() => {
    const userDetails = userService.getUser();

    const getUserDetails = (user?: User | null): UserState => {
      if (user && !user.profile) {
        return { userData: { ...user, profile: { firstName: fallbackFirstName } } };
      }

      return { userData: user };
    };

    setUser(getUserDetails(userDetails));

    return userService.on('update', ({ user }) => {
      setUser(previousUser => ({ ...previousUser, ...getUserDetails(user) }));
    });
  }, []);

  useEffect(() => {
    setUserLoading(user?.userData === undefined);
  }, [user]);

  useEffect(
    () => () => {
      userService.on('logout', () => setUser({ userData: null }));
    },
    []
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    (async () => {
      if (memberNumber) {
        await storage.setItem('memberNumber', memberNumber);

        if (hybridApp?.isReactNativeApp) {
          const memberLoyaltyDetail = { memberNumber, useQR: qrCodeEnabled };

          hybridApp.setMemberLoyaltyDetail?.(memberLoyaltyDetail);
        }
      }
    })();
  }, [memberNumber, isLoading]);

  const value = useMemo(
    () => ({
      getUserProfile,
      isGuest,
      isLoggedIn,
      logout,
      phoneVerification,
      profile,
      updateProfilePostcode,
      updateUserLocale,
      updateUserPushNotifications,
      user: { userData: user?.userData, userLoading },
    }),
    [isLoggedIn, user, profile, userLoading]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within UserProvider');
  }

  return context;
};
